// Define card decks for different estimation types
export const cardDecks = {
  fibonacci: ['0', '1', '2', '3', '5', '8', '13', '21', '34', '55', '89', '?', '☕'],
  'modified-fibonacci': ['0', '½', '1', '2', '3', '5', '8', '13', '20', '40', '100', '?', '☕'],
  't-shirt': ['XS', 'S', 'M', 'L', 'XL', 'XXL', '?', '☕']
};

// Get the deck based on type
export const getDeck = (deckType) => {
  return cardDecks[deckType] || cardDecks.fibonacci;
};

// Helper to determine if a vote is an outlier
export const isOutlier = (vote, votes, deckType) => {
  if (!votes || votes.length <= 2) return false;
  if (vote === '?' || vote === '☕') return false;
  
  // Get only the numeric votes
  const numericVotes = votes.filter(v => v !== '?' && v !== '☕');
  if (numericVotes.length <= 2) return false;
  
  // For t-shirt sizes, we consider the extremes as outliers when they're the only vote of that value
  if (deckType === 't-shirt') {
    const deck = getDeck(deckType);
    const values = deck.filter(v => v !== '?' && v !== '☕');
    const voteIndex = values.indexOf(vote);
    
    // Count votes for each value
    const counts = {};
    numericVotes.forEach(v => {
      counts[v] = (counts[v] || 0) + 1;
    });
    
    // If this is the only vote with this value AND it's at the extremes, it's an outlier
    const isExtreme = voteIndex === 0 || voteIndex === values.length - 1;
    const isOnlyVote = counts[vote] === 1;
    const totalVotes = numericVotes.length;
    
    // It's an outlier if it's the only vote of its kind at the extremes
    // AND there are at least 3 other votes
    return isOnlyVote && isExtreme && totalVotes >= 4;
  }
  
  // For numeric decks, calculate standard deviation
  const values = numericVotes.map(v => {
    if (v === '½') return 0.5;
    return parseFloat(v);
  });
  
  const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
  const squareDiffs = values.map(value => Math.pow(value - avg, 2));
  const avgSquareDiff = squareDiffs.reduce((sum, val) => sum + val, 0) / squareDiffs.length;
  const stdDev = Math.sqrt(avgSquareDiff);
  
  const voteValue = vote === '½' ? 0.5 : parseFloat(vote);
  
  // We consider a vote an outlier if:
  // 1. It's more than 1.5 standard deviations from the mean
  // 2. It's either the max or min value
  // 3. It's the only vote with this value
  const deviation = Math.abs(voteValue - avg);
  const isSignificantDeviation = deviation > stdDev * 1.5;
  const isMaxOrMin = voteValue === Math.max(...values) || voteValue === Math.min(...values);
  
  // Count how many votes have this value
  const sameValueCount = values.filter(v => v === voteValue).length;
  const isOnlyVote = sameValueCount === 1;
  
  // For stronger outlier detection, require both significant deviation and being max/min
  // But only mark as outlier if it's the only vote with this value
  return isSignificantDeviation && isMaxOrMin && isOnlyVote;
};

// Check if consensus is reached (all values are the same)
export const hasConsensus = (votes) => {
  if (!votes || votes.length <= 1) return false;
  
  const values = votes
    .filter(v => v !== '?' && v !== '☕')
    .map(v => v.toString());
  
  if (values.length <= 1) return false;
  
  return values.every(v => v === values[0]);
};

// Calculate the most common value (mode)
export const calculateMostCommonValue = (votes) => {
  if (!votes || votes.length === 0) return null;
  
  const numericVotes = votes.filter(v => v !== '?' && v !== '☕');
  if (numericVotes.length === 0) return null;
  
  const counts = {};
  numericVotes.forEach(vote => {
    counts[vote] = (counts[vote] || 0) + 1;
  });
  
  let mostCommonValue = null;
  let highestCount = 0;
  
  Object.entries(counts).forEach(([value, count]) => {
    if (count > highestCount) {
      highestCount = count;
      mostCommonValue = value;
    }
  });
  
  return mostCommonValue;
};

// Calculate consensus percentage for a specific value
export const calculateConsensusPercentage = (votes, value) => {
  if (!votes || votes.length === 0 || !value) return 0;
  
  const numericVotes = votes.filter(v => v !== '?' && v !== '☕');
  if (numericVotes.length === 0) return 0;
  
  const matchingVotes = numericVotes.filter(v => v === value);
  return (matchingVotes.length / numericVotes.length) * 100;
};

// Calculate various statistics for numeric votes
export const calculateStatistics = (votes) => {
  if (!votes || votes.length === 0) return null;
  
  const numericVotes = votes
    .filter(v => v !== '?' && v !== '☕')
    .map(v => {
      if (v === '½') return 0.5;
      return isNaN(parseFloat(v)) ? v : parseFloat(v);
    });
  
  // Only calculate for numeric values
  if (numericVotes.length === 0 || !numericVotes.every(v => typeof v === 'number')) {
    return null;
  }
  
  // Basic statistics
  const average = numericVotes.reduce((sum, val) => sum + val, 0) / numericVotes.length;
  const min = Math.min(...numericVotes);
  const max = Math.max(...numericVotes);
  const range = max - min;
  
  // Calculate median
  const sortedValues = [...numericVotes].sort((a, b) => a - b);
  const midIndex = Math.floor(sortedValues.length / 2);
  const median = sortedValues.length % 2 === 0
    ? (sortedValues[midIndex - 1] + sortedValues[midIndex]) / 2
    : sortedValues[midIndex];
  
  // Calculate standard deviation
  const squaredDiffs = numericVotes.map(value => Math.pow(value - average, 2));
  const avgSquaredDiff = squaredDiffs.reduce((sum, val) => sum + val, 0) / numericVotes.length;
  const standardDeviation = Math.sqrt(avgSquaredDiff);
  
  return {
    average,
    median,
    min,
    max,
    range,
    standardDeviation,
    count: numericVotes.length
  };
};

